/**
 * LedgerTable — ONE ledger renderer for debtors AND creditors.
 * Same columns, same colour law, expandable bill-wise breakdown on every
 * receipt/payment row, inline edit/delete. Desktop = table, mobile = cards.
 *
 * Colour law: amounts that INCREASE what is owed (Invoice for a debtor,
 * Purchase Bill for a creditor) are amber; money that SETTLES is emerald.
 * Balance is red while something is outstanding, green when settled/advance.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Pencil, Trash2, Wallet, RefreshCw } from 'lucide-react';
import LedgerEditModal from './LedgerEditModal';
import SettlementEditor from './SettlementEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const CHARGE = new Set(['Invoice', 'Purchase Bill', 'Opening Balance', 'Debit Note']);
const SETTLE = new Set(['Receipt', 'Payment', 'Payment Received', 'Payment Made', 'Credit Note', 'Opening Balance Settlement', 'Receipt (On-Account)', 'Bill Cancelled']);

const typeClass = (t) => {
  if (t === 'Invoice' || t === 'Purchase Bill') return 'bg-amber-900/40 text-amber-300 border-amber-700/40';
  if (t === 'Receipt' || t === 'Payment' || t === 'Payment Received' || t === 'Payment Made') return 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40';
  if (t === 'Credit Note' || t === 'Debit Note') return 'bg-teal-900/40 text-teal-300 border-teal-700/40';
  if (t === 'Payment Made to Debtor' || t === 'Receipt from Vendor') return 'bg-rose-900/40 text-rose-300 border-rose-700/40';
  if (t.startsWith('Opening')) return 'bg-purple-900/40 text-purple-300 border-purple-700/40';
  if (t.includes('Cancelled')) return 'bg-zinc-800 text-zinc-400 border-zinc-700 line-through';
  return 'bg-zinc-800 text-zinc-400 border-zinc-700';
};

const amountClass = (e, col) => {
  const t = e.type || '';
  if (CHARGE.has(t)) return 'text-amber-300';
  if (SETTLE.has(t)) return 'text-emerald-400';
  return col === 'debit' ? 'text-amber-300' : 'text-emerald-400';
};

/** Balance meaning depends on the role: Dr on a debtor = they owe (red); Cr on a creditor = we owe (red). */
const balanceClass = (e, role) => {
  const bal = e.running_balance || 0;
  const r = e.role || role;
  const owing = r === 'creditor' ? bal < 0 : bal > 0;
  return owing ? 'text-red-400' : 'text-emerald-400';
};

function Breakdown({ e, role }) {
  if (!e.breakdown?.length) return null;
  return (
    <div className="bg-zinc-950/70 border border-zinc-800 rounded-lg p-2.5 text-[11px] space-y-1" data-testid={`ledger-breakdown-${e.source_id}`}>
      <div className="text-zinc-500 uppercase tracking-wider text-[9px] mb-1">
        {e.type === 'Receipt' || e.type === 'Payment Received' ? 'Received against' : e.type === 'Payment' || e.type === 'Payment Made' ? 'Paid against' : (e.is_credit_note || e.is_debit_note) ? 'Adjusted against' : 'Against'}
        {(e.is_credit_note || e.is_debit_note) ? (e.restocked ? ' · goods back in stock' : ' · no stock movement') : e.payment_mode ? ` · ${e.payment_mode}` : ''}
      </div>
      {e.breakdown.map((b, i) => (
        <div key={i} className="flex items-center justify-between gap-2">
          <span className={`font-mono truncate ${b.kind === 'opening' ? 'text-purple-300' : b.kind === 'on_account' ? 'text-amber-300 italic' : 'text-zinc-300'}`}>
            {b.ref}{b.bill_date ? <span className="text-zinc-600"> · {b.bill_date}</span> : null}
            {b.kind === 'bill' && b.bill_status ? <span className={`ml-1.5 text-[9px] px-1 rounded ${b.bill_status === 'Paid' ? 'bg-emerald-900/40 text-emerald-300' : b.bill_status === 'Partial' ? 'bg-amber-900/40 text-amber-300' : 'bg-zinc-800 text-zinc-400'}`}>{b.bill_status}</span> : null}
          </span>
          <span className="font-mono text-zinc-100 whitespace-nowrap">{fmt(b.amount)}</span>
        </div>
      ))}
      {e.type === 'Invoice' || e.type === 'Purchase Bill' ? null : (
        <div className="text-[10px] text-zinc-600 pt-1 border-t border-zinc-800/60 flex justify-between">
          <span>{e.breakdown.filter(b => b.kind === 'bill').length} bill(s)</span>
          {e.on_account_amount > 0 && <span className="text-amber-300">{fmt(e.on_account_amount)} still on account</span>}
        </div>
      )}
    </div>
  );
}

export default function LedgerTable({ entries, role = 'debtor', clientId, clientName, canEdit = false, onRefresh, loading = false }) {
  const [openRows, setOpenRows] = useState({});
  const [editing, setEditing] = useState(null);
  const [settle, setSettle] = useState(null); // {voucherId, side}

  if (loading) return <div className="flex items-center justify-center h-32"><RefreshCw size={18} className="animate-spin text-zinc-500" /></div>;
  if (!entries || entries.length === 0) return <div className="text-center text-zinc-500 text-sm py-10">No transactions found</div>;

  const toggle = (k) => setOpenRows(o => ({ ...o, [k]: !o[k] }));
  const sideOf = (e) => (e.role === 'creditor' ? 'creditor' : e.role === 'debtor' ? 'debtor' : role);

  const handleEdit = (e) => {
    if (e.source_kind === 'settlement' || e.is_credit_note || e.is_debit_note) setSettle({ voucherId: e.source_id, side: sideOf(e) });
    else setEditing(e);
  };
  const handleDelete = async (e) => {
    if (!window.confirm(`Delete this ${e.type} entry?\n\n${e.date} · ${e.description}\nDr ${e.debit || 0} / Cr ${e.credit || 0}\n\nBills go back to unpaid for this money and the cashflow row is removed.`)) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/ledger-entry`, {
        params: { source_kind: e.source_kind, source_id: e.source_id, sub_id: e.sub_id || '' }, withCredentials: true,
      });
      toast.success('Entry deleted · bills + cashflow synced');
      onRefresh && (await onRefresh());
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const rowKey = (e, i) => `${e.source_kind || e.type}-${e.source_id || e.reference || ''}-${i}`;
  const expandable = (e) => (e.breakdown?.length || 0) > 0;

  const Actions = ({ e, i }) => (
    <div className="flex items-center gap-1 justify-end">
      {(e.source_kind === 'settlement' || e.is_credit_note || e.is_debit_note) && e.on_account_amount > 0 && canEdit && (
        <button onClick={() => setSettle({ voucherId: e.source_id, side: sideOf(e) })} data-testid={`allocate-ledger-${i}`}
          title="Allocate this on-account money to bills"
          className="p-1 rounded bg-amber-900/30 hover:bg-amber-900/50 text-amber-300 border border-amber-800/40 flex items-center gap-1 text-[10px] px-1.5">
          <Wallet size={11} /> Allocate
        </button>
      )}
      {canEdit && e.editable && (
        <>
          <button onClick={() => handleEdit(e)} data-testid={`edit-ledger-${i}`} title="Edit amount / date / bills"
            className="p-1 rounded bg-zinc-800/60 hover:bg-amber-900/50 text-zinc-400 hover:text-amber-300 border border-zinc-700"><Pencil size={11} /></button>
          <button onClick={() => handleDelete(e)} data-testid={`delete-ledger-${i}`} title="Delete this entry"
            className="p-1 rounded bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800/30"><Trash2 size={11} /></button>
        </>
      )}
    </div>
  );

  return (
    <>
      {/* ── desktop table ── */}
      <div className="hidden md:block">
        <table className="w-full text-xs table-fixed" data-testid="ledger-table">
          <colgroup>
            <col className="w-[88px]" /><col className="w-[150px]" /><col className="w-[120px]" /><col /><col className="w-[110px]" /><col className="w-[110px]" /><col className="w-[125px]" /><col className="w-[92px]" />
          </colgroup>
          <thead className="sticky top-0 bg-zinc-950 z-[1]">
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-wider">
              <th className="text-left px-3 py-2.5 font-medium">Date</th>
              <th className="text-left px-2 py-2.5 font-medium">Type</th>
              <th className="text-left px-2 py-2.5 font-medium">Ref</th>
              <th className="text-left px-2 py-2.5 font-medium pr-4">Particulars</th>
              <th className="text-right px-2 py-2.5 font-medium pl-4">Debit</th>
              <th className="text-right px-2 py-2.5 font-medium">Credit</th>
              <th className="text-right px-2 py-2.5 font-medium">Balance</th>
              <th className="px-2 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => {
              const k = rowKey(e, i);
              const open = !!openRows[k];
              return (
                <React.Fragment key={k}>
                  <tr className={`border-b border-zinc-800/40 hover:bg-zinc-800/20 ${e.is_cancelled ? 'opacity-60' : ''}`} data-testid={`ledger-row-${i}`}>
                    <td className="px-3 py-2 text-zinc-500 font-mono whitespace-nowrap">{e.date}</td>
                    <td className="px-2 py-2">
                      <span className={`inline-block max-w-full truncate px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${typeClass(e.type || '')}`} title={e.type}>{e.type}</span>
                    </td>
                    <td className="px-2 py-2 font-mono text-amber-400/80 text-[10px] truncate" title={e.reference}>{e.reference}</td>
                    <td className="px-2 py-2 text-zinc-400">
                      <div className="flex items-center gap-1 min-w-0">
                        {expandable(e) && (
                          <button onClick={() => toggle(k)} className="text-zinc-500 hover:text-zinc-200 flex-shrink-0" data-testid={`expand-ledger-${i}`} title="Bill-wise breakdown">
                            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </button>
                        )}
                        <span className="truncate" title={e.description}>{e.description}</span>
                        {e.is_lumpsum && <span className="text-[9px] px-1 rounded bg-emerald-900/40 text-emerald-300 flex-shrink-0">lumpsum · {e.breakdown.filter(b => b.kind === 'bill').length}</span>}
                        {e.on_account_amount > 0 && <span className="text-[9px] px-1 rounded bg-amber-900/40 text-amber-300 flex-shrink-0">on a/c {fmt(e.on_account_amount)}</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2 font-mono text-right whitespace-nowrap">{e.debit > 0 ? <span className={amountClass(e, 'debit')}>{fmt(e.debit)}</span> : <span className="text-zinc-700">—</span>}</td>
                    <td className="px-2 py-2 font-mono text-right whitespace-nowrap">{e.credit > 0 ? <span className={amountClass(e, 'credit')}>{fmt(e.credit)}</span> : <span className="text-zinc-700">—</span>}</td>
                    <td className="px-2 py-2 font-mono text-right whitespace-nowrap">
                      <span className={balanceClass(e, role)}>{fmt(Math.abs(e.running_balance || 0))} <span className="text-[9px] opacity-70">{(e.running_balance || 0) >= 0 ? 'Dr' : 'Cr'}</span></span>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap"><Actions e={e} i={i} /></td>
                  </tr>
                  {open && (
                    <tr className="border-b border-zinc-800/40 bg-zinc-900/40"><td colSpan={8} className="px-6 py-2"><Breakdown e={e} role={role} /></td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── mobile cards ── */}
      <div className="md:hidden divide-y divide-zinc-800/60" data-testid="ledger-cards">
        {entries.map((e, i) => {
          const k = rowKey(e, i);
          const open = !!openRows[k];
          return (
            <div key={k} className={`p-3 space-y-1.5 ${e.is_cancelled ? 'opacity-60' : ''}`} data-testid={`ledger-card-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-zinc-500 font-mono text-[11px] whitespace-nowrap">{e.date}</span>
                  <span className={`truncate px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${typeClass(e.type || '')}`}>{e.type}</span>
                </div>
                <span className="font-mono text-amber-400/80 text-[10px] truncate max-w-[40%]">{e.reference}</span>
              </div>
              <div className="text-zinc-400 text-xs break-words" onClick={() => expandable(e) && toggle(k)}>
                {expandable(e) && <span className="text-zinc-500 mr-1">{open ? '▾' : '▸'}</span>}{e.description}
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
                <div><div className="text-zinc-600 text-[9px] uppercase">Debit</div>{e.debit > 0 ? <span className={amountClass(e, 'debit')}>{fmt(e.debit)}</span> : <span className="text-zinc-700">—</span>}</div>
                <div><div className="text-zinc-600 text-[9px] uppercase">Credit</div>{e.credit > 0 ? <span className={amountClass(e, 'credit')}>{fmt(e.credit)}</span> : <span className="text-zinc-700">—</span>}</div>
                <div className="text-right"><div className="text-zinc-600 text-[9px] uppercase">Balance</div><span className={balanceClass(e, role)}>{fmt(Math.abs(e.running_balance || 0))} {(e.running_balance || 0) >= 0 ? 'Dr' : 'Cr'}</span></div>
              </div>
              {open && <Breakdown e={e} role={role} />}
              <Actions e={e} i={i} />
            </div>
          );
        })}
      </div>

      {editing && (
        <LedgerEditModal entry={editing} clientId={clientId} onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); onRefresh && (await onRefresh()); }} />
      )}
      {settle && (
        <SettlementEditor open clientId={clientId} clientName={clientName} side={settle.side} voucherId={settle.voucherId}
          canDelete={canEdit} onClose={() => setSettle(null)} onSaved={async () => { onRefresh && (await onRefresh()); }} />
      )}
    </>
  );
}
